import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { superAdminClientsService } from '@/services/superAdminClients.service';
import type {
  ClientListQuery,
  ClientTransactionsQuery,
  CreateOrganizationWithAdminRequest,
} from '@/types/superAdminClients';

/**
 * Super Admin Clients Hooks
 * React Query hooks for Super Admin client management
 */

// Query keys
export const superAdminClientKeys = {
  all: ['superAdminClients'] as const,
  analytics: () => [...superAdminClientKeys.all, 'analytics'] as const,
  lists: () => [...superAdminClientKeys.all, 'list'] as const,
  list: (query?: ClientListQuery) => [...superAdminClientKeys.lists(), query] as const,
  details: () => [...superAdminClientKeys.all, 'detail'] as const,
  detail: (id: string) => [...superAdminClientKeys.details(), id] as const,
  employees: (id: string) => [...superAdminClientKeys.detail(id), 'employees'] as const,
  documents: (id: string) => [...superAdminClientKeys.detail(id), 'documents'] as const,
  transactions: (query: ClientTransactionsQuery) =>
    [...superAdminClientKeys.all, 'transactions', query] as const,
};

/**
 * Hook to get client analytics summary
 */
export const useClientAnalytics = () => {
  return useQuery({
    queryKey: superAdminClientKeys.analytics(),
    queryFn: () => superAdminClientsService.getClientAnalytics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to list all clients with pagination and filters
 */
export const useClientList = (query?: ClientListQuery) => {
  return useQuery({
    queryKey: superAdminClientKeys.list(query),
    queryFn: () => superAdminClientsService.listClients(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get client detail
 */
export const useClientDetail = (clientId: string) => {
  return useQuery({
    queryKey: superAdminClientKeys.detail(clientId),
    queryFn: () => superAdminClientsService.getClientDetail(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get client employees
 */
export const useClientEmployees = (clientId: string) => {
  return useQuery({
    queryKey: superAdminClientKeys.employees(clientId),
    queryFn: () => superAdminClientsService.getClientEmployees(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get client transactions
 */
export const useClientTransactions = (query: ClientTransactionsQuery) => {
  return useQuery({
    queryKey: superAdminClientKeys.transactions(query),
    queryFn: () => superAdminClientsService.getClientTransactions(query),
    enabled: !!query.organization_id,
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent for transactions)
  });
};

/**
 * Hook to list an organization's documents
 */
export const useOrganizationDocuments = (clientId: string) => {
  return useQuery({
    queryKey: superAdminClientKeys.documents(clientId),
    queryFn: () => superAdminClientsService.listOrganizationDocuments(clientId),
    enabled: !!clientId,
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook to upload free-form documents for an organization
 */
export const useUploadOrganizationDocuments = (clientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ files, documentType }: { files: File[]; documentType: string }) =>
      superAdminClientsService.uploadOrganizationDocuments(clientId, files, documentType),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: superAdminClientKeys.documents(clientId),
      });
    },
  });
};

/**
 * Hook to delete an organization document
 */
export const useDeleteOrganizationDocument = (clientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      superAdminClientsService.deleteOrganizationDocument(clientId, documentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: superAdminClientKeys.documents(clientId),
      });
    },
  });
};

/**
 * Hook to create organization with admin
 */
export const useCreateOrganizationWithAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationWithAdminRequest) =>
      superAdminClientsService.createOrganizationWithAdmin(data),
    onSuccess: () => {
      // Invalidate client lists and analytics to reflect the new organization
      void queryClient.invalidateQueries({ queryKey: superAdminClientKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: superAdminClientKeys.analytics() });
    },
  });
};
